
import React from 'react';
import { CustomerContact } from '@/types/resellerCustomer';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { format } from 'date-fns';
import { 
  ArrowLeft, 
  Mail, 
  Phone, 
  Tag, 
  Building, 
  MapPin, 
  Calendar,
  FileText,
  Edit,
  ShoppingCart,
  MessageSquare
} from "lucide-react";

interface ContactDetailsProps {
  contact: CustomerContact;
  onBack: () => void;
}

const ContactDetails: React.FC<ContactDetailsProps> = ({
  contact,
  onBack
}) => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" onClick={onBack}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Contacts
          </Button>
          <span className="text-gray-500">|</span>
          <h2 className="text-2xl font-semibold">{contact.name}</h2>
          <Badge
            variant="outline"
            className={
              contact.status === 'active' ? 'bg-green-100 text-green-800 border-green-200' :
              contact.status === 'pending' ? 'bg-amber-100 text-amber-800 border-amber-200' :
              'bg-gray-100 text-gray-800 border-gray-200'
            }
          >
            {contact.status.charAt(0).toUpperCase() + contact.status.slice(1)}
          </Badge>
        </div>
        
        <div className="flex space-x-2">
          <Button variant="default">
            <Mail className="h-4 w-4 mr-2" />
            Email
          </Button>
          <Button variant="outline">
            <Phone className="h-4 w-4 mr-2" />
            Call
          </Button>
          <Button variant="outline">
            <MessageSquare className="h-4 w-4 mr-2" />
            SMS
          </Button>
          <Button variant="outline">
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
        </div>
      </div>
      
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="orders">Orders</TabsTrigger>
          <TabsTrigger value="communications">Communications</TabsTrigger>
          <TabsTrigger value="notes">Notes</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="space-y-6">
          {/* Contact Information */}
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-gray-500 mb-1">Name</h3>
                  <p>{contact.name}</p>
                </div>
                
                <div>
                  <h3 className="font-medium text-gray-500 mb-1">Email</h3>
                  <div className="flex items-center">
                    <Mail className="h-4 w-4 mr-2 text-gray-400" />
                    <p>{contact.email}</p>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-medium text-gray-500 mb-1">Phone</h3>
                  <div className="flex items-center">
                    <Phone className="h-4 w-4 mr-2 text-gray-400" />
                    <p>{contact.phone}</p>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-medium text-gray-500 mb-1">Company</h3>
                  <div className="flex items-center">
                    <Building className="h-4 w-4 mr-2 text-gray-400" />
                    <p>{contact.company}</p>
                    {contact.position && <span className="ml-2 text-gray-500">({contact.position})</span>}
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h3 className="font-medium text-gray-500 mb-1">Address</h3>
                  {contact.address ? (
                    <div className="flex items-start">
                      <MapPin className="h-4 w-4 mr-2 text-gray-400 mt-0.5" />
                      <div>
                        <p>{contact.address.street}</p>
                        <p>
                          {contact.address.city}, {contact.address.state} {contact.address.zip}
                        </p>
                        <p>{contact.address.country}</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-400">No address provided</p>
                  )}
                </div>
                
                <div>
                  <h3 className="font-medium text-gray-500 mb-1">Tags</h3>
                  {contact.tags && contact.tags.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {contact.tags.map((tag, index) => (
                        <Badge key={index} variant="secondary" className="flex items-center">
                          <Tag className="h-3 w-3 mr-1" />
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-400">No tags</p>
                  )}
                </div>
                
                <div>
                  <h3 className="font-medium text-gray-500 mb-1">Customer Since</h3>
                  <div className="flex items-center">
                    <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                    <p>{format(contact.createdAt, 'MMMM d, yyyy')}</p>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-medium text-gray-500 mb-1">Last Contacted</h3>
                  {contact.lastContactedAt ? (
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                      <p>{format(contact.lastContactedAt, 'MMMM d, yyyy')}</p>
                    </div>
                  ) : (
                    <p className="text-gray-400">Never contacted</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Notes */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Notes</CardTitle>
              <Button size="sm" variant="outline">
                <Edit className="h-4 w-4 mr-2" />
                Add Note
              </Button>
            </CardHeader>
            <CardContent>
              {contact.notes ? (
                <div className="p-4 bg-gray-50 rounded-md">
                  <p>{contact.notes}</p>
                </div>
              ) : (
                <div className="text-center p-8 bg-gray-50 rounded-lg">
                  <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">No notes available for this contact</p>
                  <Button size="sm" variant="outline" className="mt-2">
                    <Edit className="h-4 w-4 mr-2" />
                    Add the first note
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="orders">
          <Card>
            <CardHeader>
              <CardTitle>Order History</CardTitle>
            </CardHeader>
            <CardContent>
              {contact.orderIds.length > 0 ? (
                <div className="space-y-4">
                  {contact.orderIds.map((orderId, index) => (
                    <div 
                      key={index} 
                      className="p-4 border rounded-md flex items-center justify-between hover:bg-gray-50 cursor-pointer"
                    >
                      <div className="flex items-center">
                        <ShoppingCart className="h-5 w-5 text-primary mr-3" />
                        <div>
                          <p className="font-medium">{orderId}</p>
                          <p className="text-sm text-gray-500">
                            Ordered on {format(new Date(contact.createdAt.getTime() - (index * 30 * 24 * 60 * 60 * 1000)), 'MMMM d, yyyy')}
                          </p>
                        </div>
                      </div>
                      <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">
                        Paid
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center p-8 bg-gray-50 rounded-lg">
                  <ShoppingCart className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">No orders found for this contact</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="communications">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Communication History</CardTitle>
              <div className="flex space-x-2">
                <Button size="sm">
                  <Mail className="h-4 w-4 mr-2" />
                  New Email
                </Button>
                <Button size="sm" variant="outline">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  New SMS
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-center p-8 bg-gray-50 rounded-lg">
                <MessageSquare className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">View full communication history in the Communications tab</p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="notes">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Notes & Activity</CardTitle>
              <Button size="sm" variant="outline">
                <Edit className="h-4 w-4 mr-2" />
                Add Note
              </Button>
            </CardHeader>
            <CardContent>
              {contact.notes ? (
                <div className="p-4 bg-gray-50 rounded-md mb-4">
                  <div className="flex justify-between mb-2">
                    <p className="text-sm font-medium">Customer Note</p>
                    <p className="text-xs text-gray-500">{format(new Date(), 'MMM d, yyyy')}</p>
                  </div>
                  <p>{contact.notes}</p>
                </div>
              ) : (
                <div className="text-center p-8 bg-gray-50 rounded-lg">
                  <FileText className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-gray-500">No notes available for this contact</p>
                  <Button size="sm" variant="outline" className="mt-2">
                    <Edit className="h-4 w-4 mr-2" />
                    Add the first note
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ContactDetails;
